// client/src/components/pages/LexiStar/Replay/replaySpeed.ts
//
// The replay track coloured by speed (ST-00318): the speed line of the Trips tab's small player
// (ST-00159), retired when a trip clicked on Trips started playing in Replay itself. One GeoJSON
// LineString drawn with `lineMetrics` and a `line-gradient`: a stop per fix, placed by its share
// of the distance along the track (= line-progress) and coloured by that fix's speed band, so the
// line blends between them. Replay's Map "Speed" button switches the track to it.
//
// Map content does not follow the app theme (the basemaps do not), so the bands are literal
// colours; the legend names each band, so colour is never the only cue.
import type { ExpressionSpecification } from 'maplibre-gl';
import type { Feature, LineString } from 'geojson';
import { distanceM } from './replayLogic';

/** km/h bands, slowest first: green below 60, amber to 100, orange to 120, red above. */
export const SPEED_BANDS: readonly { label: string; color: string }[] = [
  { label: '< 60 km/h', color: '#22c55e' },
  { label: '60–100 km/h', color: '#f59e0b' },
  { label: '100–120 km/h', color: '#f97316' },
  { label: '> 120 km/h', color: '#ef4444' },
];

/** A speed (km/h; none counts as standing) → its band's colour. */
export function speedColor(kmh: number | null | undefined): string {
  const s = kmh || 0;
  if (s < 60) return SPEED_BANDS[0]!.color;
  if (s <= 100) return SPEED_BANDS[1]!.color;
  if (s <= 120) return SPEED_BANDS[2]!.color;
  return SPEED_BANDS[3]!.color;
}

export interface SpeedLine {
  line: Feature<LineString>;
  /** The line-gradient expression; null when the track does not move (one colour then). */
  gradient: ExpressionSpecification | null;
  /** The first fix's colour: the line's colour without a gradient. */
  color: string;
}

/**
 * The track as one speed-coloured line, or null with fewer than two distinct positions.
 * Consecutive repeats are dropped so the distance along the line strictly increases (the
 * gradient's stops must ascend).
 */
export function speedLine(
  track: readonly { lat: number; lon: number; speed_kmh: number | null }[]
): SpeedLine | null {
  const fixes: { lat: number; lon: number; speed_kmh: number | null }[] = [];
  for (const p of track) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    const prev = fixes[fixes.length - 1];
    if (prev && Math.abs(prev.lat - p.lat) < 1e-9 && Math.abs(prev.lon - p.lon) < 1e-9) continue;
    fixes.push(p);
  }
  if (fixes.length < 2) return null;

  const along = [0];
  for (let i = 1; i < fixes.length; i++) along[i] = along[i - 1]! + distanceM(fixes[i - 1]!, fixes[i]!);
  const total = along[fixes.length - 1]!;
  const line: Feature<LineString> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: fixes.map((p) => [p.lon, p.lat]) },
  };
  const color = speedColor(fixes[0]!.speed_kmh);
  if (!(total > 0)) return { line, gradient: null, color };

  const stops: (number | string)[] = [];
  let last = -1;
  fixes.forEach((p, i) => {
    let at = along[i]! / total;
    if (at <= last) at = Math.min(1, last + 1e-6); // floating-point ties
    last = at;
    stops.push(at, speedColor(p.speed_kmh));
  });
  return {
    line,
    gradient: ['interpolate', ['linear'], ['line-progress'], ...stops] as unknown as ExpressionSpecification,
    color,
  };
}
